import React, { useState, useEffect, useCallback } from 'react';
import { invoiceApi, paymentApi, projectApi, taxApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, Trash, Receipt, CurrencyDollar, Paperclip, File, FilePdf, Image, X, DownloadSimple, Eye, CaretDown, CaretUp } from '@phosphor-icons/react';

import { fmtCurrency } from '../utils/currencyFormat';

const MAX_FILE_SIZE_MB = 10;
const MAX_ATTACHMENTS = 5;

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function FileIcon({ type }) {
  if (type === 'pdf') return <FilePdf className="w-4 h-4 text-[#DC2626]" />;
  if (['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(type)) return <Image className="w-4 h-4 text-[#2E7D32]" />;
  return <File className="w-4 h-4 text-[#666]" />;
}

function InvoiceAttachments({ invoiceId, attachments = [], onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert(`File too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await invoiceApi.uploadAttachment(invoiceId, formData);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await invoiceApi.deleteAttachment(invoiceId, fileId);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const isImage = (type) => ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(type);

  if (attachments.length === 0 && !expanded) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <label
          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-dashed border-[#CCC] text-[#666] cursor-pointer hover:border-[#002FA7] hover:text-[#002FA7] hover:bg-[#F5F7FF] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          data-testid={`upload-attachment-${invoiceId}`}
        >
          {uploading ? <Spinner size="sm" /> : <Paperclip className="w-3.5 h-3.5" />}
          <span>Attach file</span>
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
        <span className="text-[9px] text-[#999]">Max {MAX_FILE_SIZE_MB} MB | PDF, Images</span>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline"
        data-testid={`toggle-attachments-${invoiceId}`}
      >
        <Paperclip className="w-3.5 h-3.5" />
        {attachments.length} attachment{attachments.length !== 1 ? 's' : ''}
        {expanded ? <CaretUp className="w-3 h-3" /> : <CaretDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-1.5">
          {attachments.map((att) => (
            <div key={att.id} className="flex items-center gap-2 p-2 bg-[#F9F9F9] rounded-md group" data-testid={`inv-attachment-${att.id}`}>
              <FileIcon type={att.type} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#333] truncate">{att.original_name}</p>
                <p className="text-[9px] text-[#999]">{formatFileSize(att.size)} — {att.uploaded_by_name}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {isImage(att.type) && (
                  <button onClick={() => setLightbox(att)} className="p-1 text-[#999] hover:text-[#002FA7]" data-testid={`view-attachment-${att.id}`}>
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                )}
                <a href={invoiceApi.getFileUrl(att.filename)} download={att.original_name} target="_blank" rel="noreferrer" className="p-1 text-[#999] hover:text-[#002FA7]" data-testid={`download-attachment-${att.id}`}>
                  <DownloadSimple className="w-3.5 h-3.5" />
                </a>
                <button onClick={() => handleDelete(att.id)} className="p-1 text-[#999] hover:text-[#FF3333] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-inv-attachment-${att.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}

          {attachments.length < MAX_ATTACHMENTS && (
            <label
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-dashed border-[#CCC] text-[#666] cursor-pointer hover:border-[#002FA7] hover:text-[#002FA7] hover:bg-[#F5F7FF] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              data-testid={`upload-more-${invoiceId}`}
            >
              {uploading ? <Spinner size="sm" /> : <Plus className="w-3.5 h-3.5" />}
              <span>Add file</span>
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          )}
          <p className="text-[9px] text-[#999]">{attachments.length}/{MAX_ATTACHMENTS} files (max {MAX_FILE_SIZE_MB} MB each, PDF or images)</p>
        </div>
      )}

      {/* Image Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="invoice-lightbox">
          <div className="relative max-w-3xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={invoiceApi.getFileUrl(lightbox.filename)}
              alt={lightbox.original_name}
              className="max-w-full max-h-[80vh] rounded-lg object-contain"
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <a href={invoiceApi.getFileUrl(lightbox.filename)} download={lightbox.original_name} target="_blank" rel="noreferrer" className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors">
                <DownloadSimple className="w-4 h-4 text-[#333]" />
              </a>
              <button onClick={() => setLightbox(null)} className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors" data-testid="close-inv-lightbox-btn">
                <X className="w-4 h-4 text-[#333]" />
              </button>
            </div>
            <p className="text-center text-white text-sm mt-2">{lightbox.original_name}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');
  const [taxRates, setTaxRates] = useState([]);
  const [trn, setTrn] = useState('');

  // Invoice form
  const [showInvModal, setShowInvModal] = useState(false);
  const [invForm, setInvForm] = useState({ project_id: '', amount: '', tax_amount: '0', tax_rate_id: '', invoice_date: '', due_date: '', description: '' });
  const [invError, setInvError] = useState('');
  const [invSaving, setInvSaving] = useState(false);

  // Payment form
  const [showPmtModal, setShowPmtModal] = useState(false);
  const [pmtInvoice, setPmtInvoice] = useState(null);
  const [pmtForm, setPmtForm] = useState({ amount: '', payment_date: '', payment_method: 'bank_transfer', reference: '' });
  const [pmtError, setPmtError] = useState('');
  const [pmtSaving, setPmtSaving] = useState(false);

  useEffect(() => {
    Promise.all([projectApi.list(), taxApi.listRates(true), taxApi.getTRN()])
      .then(([p, t, trnRes]) => { setProjects(p.data); setTaxRates(t.data); setTrn(trnRes.data.trn || ''); })
      .finally(() => setLoading(false));
  }, []);

  const loadInvoices = useCallback(async () => {
    const res = await invoiceApi.list(filterProject || undefined);
    // Load attachments for each invoice
    const invoicesWithAttachments = await Promise.all(
      res.data.map(async (inv) => {
        try {
          const attRes = await invoiceApi.listAttachments(inv.id);
          return { ...inv, attachments: attRes.data };
        } catch {
          return { ...inv, attachments: [] };
        }
      })
    );
    setInvoices(invoicesWithAttachments);
  }, [filterProject]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const fmt = (n) => fmtCurrency(n);

  // Invoice CRUD
  const openInvCreate = () => {
    const defaultRate = taxRates.find(r => r.is_default);
    setInvForm({
      project_id: filterProject || '', amount: '', tax_amount: '0',
      tax_rate_id: defaultRate ? defaultRate.id : '',
      invoice_date: new Date().toISOString().split('T')[0], due_date: '', description: ''
    });
    setInvError(''); setShowInvModal(true);
  };

  const handleTaxRateChange = (rateId) => {
    const rate = taxRates.find(r => r.id === rateId);
    const amt = parseFloat(invForm.amount) || 0;
    const taxAmt = rate ? Math.round(amt * rate.percentage / 100 * 100) / 100 : 0;
    setInvForm({ ...invForm, tax_rate_id: rateId, tax_amount: String(taxAmt) });
  };

  const handleInvAmountChange = (val) => {
    const amt = parseFloat(val) || 0;
    const rate = taxRates.find(r => r.id === invForm.tax_rate_id);
    const taxAmt = rate ? Math.round(amt * rate.percentage / 100 * 100) / 100 : parseFloat(invForm.tax_amount) || 0;
    setInvForm({ ...invForm, amount: val, tax_amount: String(taxAmt) });
  };

  const handleInvSubmit = async (e) => {
    e.preventDefault(); setInvError(''); setInvSaving(true);
    try {
      const rate = taxRates.find(r => r.id === invForm.tax_rate_id);
      await invoiceApi.create({
        project_id: invForm.project_id,
        amount: parseFloat(invForm.amount) || 0,
        tax_amount: parseFloat(invForm.tax_amount) || 0,
        tax_rate_id: invForm.tax_rate_id || null,
        tax_rate_percentage: rate ? rate.percentage : null,
        tax_rate_name: rate ? rate.tax_name : null,
        trn: trn || null,
        invoice_date: invForm.invoice_date,
        due_date: invForm.due_date || null,
        description: invForm.description || null,
      });
      setShowInvModal(false); loadInvoices();
    } catch (err) { setInvError(err.response?.data?.detail || 'Failed'); }
    finally { setInvSaving(false); }
  };

  const handleInvDelete = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try { await invoiceApi.delete(id); loadInvoices(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // Payment
  const openPayment = (inv) => {
    setPmtInvoice(inv);
    setPmtForm({ amount: String(inv.balance_due), payment_date: new Date().toISOString().split('T')[0], payment_method: 'bank_transfer', reference: '' });
    setPmtError(''); setShowPmtModal(true);
  };

  const handlePmtSubmit = async (e) => {
    e.preventDefault(); setPmtError(''); setPmtSaving(true);
    try {
      await paymentApi.create({
        invoice_id: pmtInvoice.id, amount: parseFloat(pmtForm.amount) || 0,
        payment_date: pmtForm.payment_date, payment_method: pmtForm.payment_method, reference: pmtForm.reference
      });
      setShowPmtModal(false); loadInvoices();
    } catch (err) { setPmtError(err.response?.data?.detail || 'Failed'); }
    finally { setPmtSaving(false); }
  };

  const totals = invoices.reduce((a, i) => ({
    invoiced: a.invoiced + (i.total_amount || 0),
    paid: a.paid + (i.amount_paid || 0),
    due: a.due + (i.balance_due || 0)
  }), { invoiced: 0, paid: 0, due: 0 });

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Invoices & Payments</h1>
          <p className="text-sm text-[#666]">Revenue tracking with automatic journal entries</p>
        </div>
        <Button onClick={openInvCreate} data-testid="add-invoice-btn"><Plus className="w-4 h-4 mr-2" /> New Invoice</Button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <Select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="w-64" data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      {invoices.length > 0 && (
        <div className="grid grid-cols-3 gap-3" data-testid="invoice-totals">
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-center">
            <p className="text-[10px] text-[#999] uppercase">Invoiced</p>
            <p className="text-lg font-bold text-[#002FA7]">{fmt(totals.invoiced)}</p>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-center">
            <p className="text-[10px] text-[#999] uppercase">Received</p>
            <p className="text-lg font-bold text-[#00C853]">{fmt(totals.paid)}</p>
          </div>
          <div className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-2.5 text-center">
            <p className="text-[10px] text-[#999] uppercase">Outstanding</p>
            <p className="text-lg font-bold text-[#FF3333]">{fmt(totals.due)}</p>
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <Card className="p-8 text-center text-[#666]">No invoices yet</Card>
      ) : (
        <div className="space-y-2">
          {invoices.map(inv => (
            <Card key={inv.id} className="p-3 sm:p-4" data-testid={`invoice-row-${inv.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Receipt className="w-4 h-4 text-[#002FA7]" />
                    <span className="font-semibold text-sm">{inv.invoice_number}</span>
                    <Badge variant={inv.status === 'paid' ? 'success' : inv.status === 'partial' ? 'warning' : 'primary'}>{inv.status}</Badge>
                    {inv.attachments?.length > 0 && (
                      <span className="text-[10px] bg-[#E3F2FD] text-[#1565C0] px-1.5 py-0.5 rounded flex items-center gap-0.5">
                        <Paperclip className="w-3 h-3" /> {inv.attachments.length}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#666]">
                    <span>{inv.project_name}</span>
                    <span>{inv.invoice_date}</span>
                    {inv.description && <span>{inv.description}</span>}
                    {inv.tax_rate_name && <span className="text-[#DC2626]">{inv.tax_rate_name}</span>}
                    {inv.trn && <span>TRN: {inv.trn}</span>}
                  </div>

                  {/* Invoice Attachments */}
                  <InvoiceAttachments
                    invoiceId={inv.id}
                    attachments={inv.attachments || []}
                    onRefresh={loadInvoices}
                  />
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#0A0A0A]">{fmt(inv.total_amount)}</p>
                    <p className="text-[10px] text-[#666]">
                      Base: {fmt(inv.amount)}{inv.tax_amount > 0 && <> + Tax: {fmt(inv.tax_amount)}</>}
                    </p>
                    <p className="text-[10px] text-[#666]">Paid: {fmt(inv.amount_paid)} | Due: <span className={inv.balance_due > 0 ? 'text-[#FF3333] font-bold' : ''}>{fmt(inv.balance_due)}</span></p>
                  </div>
                  {inv.balance_due > 0.01 && (
                    <Button size="sm" onClick={() => openPayment(inv)} data-testid={`pay-invoice-${inv.id}`}>
                      <CurrencyDollar className="w-3 h-3 mr-1" /> Pay
                    </Button>
                  )}
                  {inv.status !== 'paid' && inv.amount_paid === 0 && (
                    <button onClick={() => handleInvDelete(inv.id)} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`delete-invoice-${inv.id}`}><Trash className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Invoice Modal */}
      <Modal isOpen={showInvModal} onClose={() => setShowInvModal(false)} title="Create Invoice">
        <form onSubmit={handleInvSubmit} className="space-y-3" data-testid="invoice-form">
          {invError && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{invError}</div>}
          <Select label="Project" value={invForm.project_id} onChange={(e) => setInvForm({ ...invForm, project_id: e.target.value })} required data-testid="inv-project">
            <option value="">-- Select --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Input label="Amount" type="number" min="0.01" step="0.01" value={invForm.amount} onChange={(e) => handleInvAmountChange(e.target.value)} required data-testid="inv-amount" />
          <Select label="Tax Rate" value={invForm.tax_rate_id} onChange={(e) => handleTaxRateChange(e.target.value)} data-testid="inv-tax-rate">
            <option value="">No Tax</option>
            {taxRates.map(r => <option key={r.id} value={r.id}>{r.tax_name} ({r.percentage}%)</option>)}
          </Select>
          <Input label="Tax Amount" type="number" min="0" step="0.01" value={invForm.tax_amount} onChange={(e) => setInvForm({ ...invForm, tax_amount: e.target.value })} data-testid="inv-tax" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Invoice Date" type="date" value={invForm.invoice_date} onChange={(e) => setInvForm({ ...invForm, invoice_date: e.target.value })} required data-testid="inv-date" />
            <Input label="Due Date" type="date" value={invForm.due_date} onChange={(e) => setInvForm({ ...invForm, due_date: e.target.value })} data-testid="inv-due" />
          </div>
          <Input label="Description" value={invForm.description} onChange={(e) => setInvForm({ ...invForm, description: e.target.value })} data-testid="inv-desc" />
          {invForm.amount && (
            <div className="text-right text-sm font-semibold text-[#002FA7]">Total: {fmt((parseFloat(invForm.amount) || 0) + (parseFloat(invForm.tax_amount) || 0))}</div>
          )}
          <p className="text-[10px] text-[#999]">You can attach supporting documents (LPO, receipts) after creating the invoice (max {MAX_ATTACHMENTS} files, {MAX_FILE_SIZE_MB} MB each)</p>
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowInvModal(false)}>Cancel</Button>
            <Button type="submit" disabled={invSaving} data-testid="save-invoice-btn">{invSaving ? 'Creating...' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={showPmtModal} onClose={() => setShowPmtModal(false)} title={`Record Payment — ${pmtInvoice?.invoice_number || ''}`}>
        <form onSubmit={handlePmtSubmit} className="space-y-3" data-testid="payment-form">
          {pmtError && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{pmtError}</div>}
          <div className="bg-[#F9F9F9] rounded-lg p-2 text-sm">
            Invoice Total: <b>{fmt(pmtInvoice?.total_amount)}</b> | Already Paid: <b>{fmt(pmtInvoice?.amount_paid)}</b> | <span className="text-[#FF3333] font-bold">Due: {fmt(pmtInvoice?.balance_due)}</span>
          </div>
          <Input label="Payment Amount ($)" type="number" min="0.01" step="0.01" value={pmtForm.amount} onChange={(e) => setPmtForm({ ...pmtForm, amount: e.target.value })} required data-testid="pmt-amount" />
          <Input label="Payment Date" type="date" value={pmtForm.payment_date} onChange={(e) => setPmtForm({ ...pmtForm, payment_date: e.target.value })} required data-testid="pmt-date" />
          <Select label="Method" value={pmtForm.payment_method} onChange={(e) => setPmtForm({ ...pmtForm, payment_method: e.target.value })} data-testid="pmt-method">
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cash">Cash</option>
            <option value="check">Check</option>
            <option value="online">Online</option>
          </Select>
          <Input label="Reference" value={pmtForm.reference} onChange={(e) => setPmtForm({ ...pmtForm, reference: e.target.value })} placeholder="Check/transfer number" data-testid="pmt-ref" />
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowPmtModal(false)}>Cancel</Button>
            <Button type="submit" disabled={pmtSaving} data-testid="save-payment-btn">{pmtSaving ? 'Processing...' : 'Record Payment'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
